<template>
  <UIModal
    class="ModalGptShare"
    data-testid="gpt-share-modal"
    :isDismissable="isDismissable"
    v-bind="$attrs"
    @close="emit('close')"
    @enter="emit('close')"
  >
    <template #title>
      <slot name="title">{{ label }}</slot>
    </template>

    <template #content>
      <div class="ModalGptShare__form">
        <DropdownGptSharing
          ref="dropdownRef"
          v-model="isPublic"
          class="ModalGptShare__dropdown-gpt-sharing"
          :isFoundever="isFoundever"
          @update:modelValue="onPrivacyToggle()"
        />

        <p v-if="isFoundever" class="ModalGptShare__foundever-notice">
          This GPT is published by Foundever, which automatically makes it
          accessible to all users. To change the access, make sure to turn off
          the "Published by Foundever" option and set a different sharing
          setting.
        </p>

        <template v-if="!isPublic && !isFoundever">
          <div class="ModalGptShare__user-search-container">
            <GptShareUserSearch
              class="ModalGptShare__user-search"
              :users="users"
              :usersWithAccess="usersWithAccess"
              @addUser="addUser"
              @removeUser="removeSingleUser"
            />

            <UIButton
              class="ModalGptShare__user-search-button"
              data-testid="gpt-share-submit"
              :isLoading="isLoading"
              :isDisabled="users.length === 0"
              theme="primary"
              type="submit"
              @click.prevent="share()"
            >
              Share
            </UIButton>
          </div>

          <div class="ModalGptShare__permissions-container">
            <div class="ModalGptShare__permissions-title">
              Specific access:

              <button
                v-if="usersWithAccess.length > 1"
                class="ModalGptShare__permissions-title-remove-all"
                @click.prevent="modalRemoveAllUsers.open()"
              >
                Remove all
              </button>
            </div>

            <div
              class="ModalGptShare__users-list"
              data-testid="gpt-share-access-list"
            >
              <div
                v-for="userWithAccess in usersWithAccess"
                :key="getUserIdentity(userWithAccess)"
                class="ModalGptShare__users-list-item"
                data-testid="gpt-share-access-user"
              >
                <UITextEllipsis
                  :title="capitalizeAllWords(userWithAccess.name || '')"
                >
                  {{ capitalizeAllWords(userWithAccess.name || '') }}
                </UITextEllipsis>
                <component :is="!userWithAccess.email ? 'i' : 'span'">
                  ({{ userWithAccess.email || 'No email for this user' }})
                </component>

                <UITag
                  v-if="userWithAccess.id === user?.id"
                  class="ModalGptShare__users-list-item-tag"
                  size="md"
                  theme="neutral"
                  hierarchy="secondary"
                >
                  You
                </UITag>

                <UIButton
                  v-if="userWithAccess.id !== user?.id"
                  data-testid="gpt-share-access-remove"
                  leadIcon="close"
                  class="ModalGptShare__users-list-item-remove"
                  aria-label="Remove user"
                  theme="secondary"
                  size="sm"
                  @click="modalRemoveUser.open(userWithAccess)"
                />
              </div>
            </div>
          </div>
        </template>

        <div v-if="isCreating || isFoundever" class="ModalGptShare__actions">
          <UIButton v-if="isCreating" @click.prevent="onSubmit()">
            Save & Create
          </UIButton>

          <UIButton
            v-if="isFoundever && gpt.id"
            :to="{ name: 'GptUpdate', params: { uuid: gpt.id } }"
          >
            Edit GPT settings
          </UIButton>
        </div>
      </div>
    </template>
  </UIModal>

  <ModalConfirm
    v-if="modalRemoveUser.modalData"
    :isOpen="modalRemoveUser.isOpen"
    confirmButtonLabel="Remove"
    @close="modalRemoveUser.close()"
    @confirm="removeUser(modalRemoveUser.modalData)"
  >
    <template #title>
      Remove sharing with {{ modalRemoveUser.modalData.name }}?
    </template>
    <template #content>
      You’re about to remove sharing this GPT with
      {{ modalRemoveUser.modalData.name }}. They’ll lose access immediately.
    </template>
  </ModalConfirm>

  <ModalConfirm
    :isOpen="modalRemoveAllUsers.isOpen"
    @close="modalRemoveAllUsers.close()"
    @confirm="removeAllUsers()"
  >
    <template #title>
      {{
        gpt.is_public
          ? 'Remove sharing with everyone?'
          : 'Remove sharing with selected users?'
      }}
    </template>

    <template #content>
      <template v-if="gpt.is_public">
        You’re about to remove sharing this GPT with everyone and restrict
        access to private (only you).
      </template>

      <template v-else>
        You’re about to remove sharing this GPT with
        {{ usersWithAccess.length - 1 }} users. They’ll lose access immediately.
      </template>
    </template>
  </ModalConfirm>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { UIButton, UIModal, UITag } from '@foundever/ui-lib'
import { storeToRefs } from 'pinia'

import type { IGptPermissions, IGptListItem } from '@/apiClient/types/gpt'
import type { ISelectedUser } from '@/apiClient/types/user'
import { useModal } from '@/composables/useModal'
import { useAuthStore } from '@/store/auth'
import { capitalizeAllWords } from '@/utils'
import GptShareUserSearch from '@/views/pages/Gpt/List/components/GptShareUserSearch.vue'

import ModalConfirm from './ModalConfirm.vue'
import DropdownGptSharing from '../dropdowns/DropdownGptSharing.vue'
import UITextEllipsis from '../ui/typography/UITextEllipsis.vue'

defineOptions({
  inheritAttrs: false,
})
const emit = defineEmits(['close', 'confirm'])
const props = withDefaults(
  defineProps<{
    isLoading?: boolean
    isCreating?: boolean
    gpt: IGptListItem
  }>(),
  {
    isLoading: false,
    title: 'Share GPT',
  },
)

// TODO: isFoundever is only public if it is BOTH owned by Foundever AND
// has the is_public flag set to true
const isFoundever = computed(
  () => props.gpt.is_foundever && props.gpt.is_public,
)

const label = computed(() =>
  props.isCreating ? 'Almost done! Choose your sharing settings.' : 'Share GPT',
)

const { user } = storeToRefs(useAuthStore())
const isPublic = ref(false)

/**
 * Your own row in the GPT's permission list, present only when the GPT was shared
 * *with* you. The owner has no row of their own, and neither does an admin acting
 * on someone else's GPT — for them the "You" entry in `usersWithAccess` is purely
 * synthetic, added for display.
 */
const ownPermission = computed(() =>
  (props.gpt.permissions || []).find(
    (permission) => permission.user_id === user.value?.id,
  ),
)

// Keep the level each user already has. Remapping the whole list to `read` would
// silently demote a write collaborator — including yourself — on every share.
const permissionLevel = (
  selectedUser: ISelectedUser,
): IGptPermissions['permission'] =>
  (props.gpt.permissions || []).find(
    (permission) =>
      permission.user_id === selectedUser.id ||
      (!!permission.employee_id &&
        permission.employee_id === selectedUser.employee_id),
  )?.permission || 'read'

const removeAllUsers = () => {
  isPublic.value = false
  onSubmit(true)
  modalRemoveAllUsers.close()
}

const users = ref<ISelectedUser[]>([])
const usersWithAccess = ref<ISelectedUser[]>([])

const getUserIdentity = (user: ISelectedUser) =>
  user.id || user.employee_id || user.email || user.name

watch(
  () => props.gpt,
  (gpt) => {
    if (!gpt) return

    isPublic.value = gpt.is_public || false

    const allUsers: ISelectedUser[] = [
      {
        id: user.value?.id || '',
        email: user.value?.email || '',
        name: user.value?.name || '',
        employee_id: user.value?.employee_id || '',
      },
      ...(gpt?.permissions || []).map((permission) => ({
        id: permission.user_id,
        email: permission.user_email,
        name: permission.user_name,
        employee_id: permission.employee_id || '',
      })),
    ]

    const seenUsers = new Set<string>()
    usersWithAccess.value = allUsers.filter((user) => {
      const userIdentity = getUserIdentity(user)

      if (!userIdentity.trim()) {
        return false
      }
      if (seenUsers.has(userIdentity)) {
        return false
      }
      seenUsers.add(userIdentity)
      return true
    })
  },
  { immediate: true },
)
const addUser = (user: ISelectedUser) => {
  users.value.push(user)
}
const removeUser = (user: ISelectedUser) => {
  if (props.isCreating) {
    usersWithAccess.value = usersWithAccess.value.filter(
      (u) => getUserIdentity(u) !== getUserIdentity(user),
    )
  } else {
    onSubmit(false, user)
  }
  modalRemoveUser.close()
}

const removeSingleUser = (user: ISelectedUser) => {
  users.value = users.value.filter(
    (u) => getUserIdentity(u) !== getUserIdentity(user),
  )
}

const share = () => {
  if (props.isCreating) {
    usersWithAccess.value = usersWithAccess.value.concat(users.value)
    users.value = []
  } else {
    onSubmit()
  }
}

const onPrivacyToggle = () => {
  if (!props.isCreating) {
    onSubmit()
  }
}

const onSubmit = (reset?: boolean, excludeUser?: ISelectedUser) => {
  const usersToAdd = users.value.filter(
    (user) =>
      !excludeUser || getUserIdentity(user) !== getUserIdentity(excludeUser),
  )
  const permissions: IGptPermissions[] = (usersToAdd || []).map((user) => ({
    user_id: user.id || '',
    user_email: user.email,
    user_name: user.name,
    employee_id: user.employee_id,
    permission: 'read',
  }))

  // `PUT /gpts/{id}` replaces the whole permission set — the API deletes every row
  // the payload omits — so an existing row of your own has to be re-sent. Drop the
  // "You" entry only when it is synthetic (owner / admin), otherwise sharing a GPT
  // that was shared with you revokes your own access and it disappears from the
  // "Shared with you" tab.
  const gptPermissions = usersWithAccess.value
    .filter((_user) => _user.id !== user.value?.id || !!ownPermission.value)
    .map((user) => ({
      user_id: user.id || '',
      employee_id: user.employee_id || '',
      permission: permissionLevel(user),
      user_email: user.email,
      user_name: user.name,
    }))

  const permrissionsPayload = [
    ...(permissions || []),
    ...(gptPermissions.filter(
      (permission) =>
        !excludeUser ||
        (permission.user_id || permission.employee_id) !==
          getUserIdentity(excludeUser),
    ) || []),
  ]

  // "Remove all" revokes everyone the GPT was shared with. Your own row is not
  // someone else's access to revoke — keep it, or you lock yourself out.
  const ownPermissionPayload = ownPermission.value
    ? [
        {
          user_id: ownPermission.value.user_id,
          employee_id: ownPermission.value.employee_id || '',
          permission: ownPermission.value.permission,
          user_email: ownPermission.value.user_email,
          user_name: ownPermission.value.user_name,
        },
      ]
    : []

  const payload = {
    permissions: reset ? ownPermissionPayload : permrissionsPayload,
    is_public: isPublic.value,
  }
  emit('confirm', payload)
  users.value = []
}

const modalRemoveUser = useModal<ISelectedUser>()
const modalRemoveAllUsers = useModal()
const dropdownRef = ref<InstanceType<typeof DropdownGptSharing> | null>(null)

// ui-lib modals each listen for Escape on `document`, so Escape pressed in a
// nested confirm would also dismiss this modal — keep it open while a confirm
// is showing, and while a request is in flight.
const isDismissable = computed(
  () =>
    !props.isLoading &&
    !modalRemoveUser.isOpen &&
    !modalRemoveAllUsers.isOpen &&
    !dropdownRef.value?.isConfirmOpen,
)
</script>

<style lang="scss">
.ModalGptShare.UIModalContainer {
  .ModalGptShare__form {
    padding-bottom: 1rem;
  }

  .ModalGptShare__user-search-container {
    margin-top: 0.5rem;
    display: grid;
    gap: 0.25rem;
    width: 100%;
    grid-template-columns: 1fr 4.375rem;
  }

  .ModalGptShare__user-search {
    width: 100%;
  }

  .ModalGptShare__dropdown-gpt-sharing {
    width: 100%;
  }

  .ModalGptShare__foundever-notice {
    @include font-size-10px;
    color: var(--text-tertiary);
    font-weight: 400;
    margin-top: 1rem;
  }

  .ModalGptShare__permissions-container {
    margin-top: 1rem;
  }

  .ModalGptShare__permissions-title {
    @include font-size-14px;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--text-primary);
    margin-bottom: 0.5rem;
  }

  .ModalGptShare__permissions-title-remove-all {
    color: var(--text-primary);
    font-weight: 400;
    padding: 0.25rem 0.5rem;
  }

  .ModalGptShare__users-list {
    @include customScrollbar;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    max-height: 8.75rem;
  }

  .ModalGptShare__users-list-item {
    @include font-size-12px;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: var(--text-secondary);
    font-weight: 500;
    padding: 0.25rem 0;
    height: 1.5rem;
    white-space: nowrap;
  }

  .ModalGptShare__users-list-item-tag {
    margin: 0 auto 0 0.25rem;
    white-space: nowrap;
  }

  .ModalGptShare__users-list-item-remove {
    margin-left: auto;
  }

  .ModalGptShare__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
  }
}
</style>
